import {
  EllipsisPlacement,
  PersonaMessages,
  SuspenseLoader,
  TextWithEllipsis,
  useName
} from '@bechtel/client-common';
import { belowBreakpoint, Box, Icon, IconSize, Text, Theme, useMediaQuery, useSideSheetState } from '@bechtel/gds';
import { CrewMemberAssignment } from '@bechtel/types';
import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { groupBy, map, some, uniqBy } from 'lodash';
import { FC, Suspense, useContext, useState } from 'react';
import { ThemeContext } from 'styled-components';
import { AssignWorkOwner } from '~/components/organisms/assign-work-owner';
import { useWorkPackagePermissions } from '~/hooks/use-work-package-permissions';
import {
  AddCrewStyled,
  AvatarRegularStyled,
  MultiUserIcon,
  OwnerActionButtonStyled,
  OwnerListContainerStyled,
  OwnerListOuterStyled,
  OwnerNameStyled,
  OwnerStyled,
} from './styled';
import {
  ActionButtonContentProps,
  OwnerListProps,
  WorkPackageOwnerListProps,
  WorkPackagePrimaryOwnerListProps,
} from './types';
interface AdditionalProps {
  multipleOwners?: boolean;
}

export const WorkPackageOwner: FC<CrewMemberAssignment & AdditionalProps> = ({
  multipleOwners,
  crewMember,
  persona,
}) => {
  const { getFullName } = useName();
  const { i18n } = useLingui();
  return (
    <OwnerStyled data-testid="work-owner-item">
      <Box flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }} justifyContent={{ sm: 'space-between' }}>
        {multipleOwners ? (
          <MultiUserIcon data-testid="multiple-selected">
            <Icon size={IconSize.MEDIUM} icon="users" />
          </MultiUserIcon>
        ) : (
          crewMember && (
            <AvatarRegularStyled
              data-testid="avatar-regular-owner"
              src={crewMember.imageUrl ? crewMember.imageUrl : undefined}
            />
        ))}
        {!multipleOwners && !crewMember && (
          <AddCrewStyled data-testid="work-owner-unassigned">
            <Icon size={IconSize.MEDIUM} icon="user-plus" />
          </AddCrewStyled>
        )}
        <OwnerNameStyled>
          <TextWithEllipsis
            size="300"
            color="white"
            weight="600"
            ellipsisPlacement={EllipsisPlacement.END}
            data-testid="work-owner-name"
          >
            {multipleOwners
              ? t`Multiple`
              : crewMember
                ? getFullName(crewMember.firstName, crewMember.lastName)
                : t`Unassigned`}
          </TextWithEllipsis>
        </OwnerNameStyled>

        <Text size="200" color="white" data-testid="work-owner-persona">
          {i18n._(PersonaMessages[persona])}
        </Text>
      </Box>
    </OwnerStyled>
  );
};

export const OwnerList: FC<OwnerListProps> = ({ owners, visible }) => {
  const primaryOwnersByPersona = Object.values(groupBy(owners, 'persona'));
  return (
    <Box data-testid="work-owner-list" hidden={!visible}>
      {map(primaryOwnersByPersona, (owner, index) => {
        const uniquePersona = owner.length > 0 ? uniqBy(owner, 'crewMember.id') : [];

        return (
          <WorkPackageOwner
            key={index}
            multipleOwners={uniquePersona.length > 1}
            {...uniquePersona[0]}
            persona={owner[0]?.persona}
          />
        );
      })}
    </Box>
  );
};

const ActionButtonContent: FC<ActionButtonContentProps> = ({ icon, buttonText }) => {
  return (
    <>
      <Box mr={{ sm: '400' }}>
        <Icon icon={icon} size={IconSize.MEDIUM} />
      </Box>
      <Text size="300" weight="600">
        {buttonText}
      </Text>
    </>
  );
};

export const isAnyOwnerAssigned = (crewMemberAssignments: CrewMemberAssignment[]): boolean => {
  return some(crewMemberAssignments, (assignment) => Boolean(assignment.crewMember));
};

//FIXME : TODO: Workpackage parameter already has owners, no need to pass them as separate parameters
//FIXME: TODO: ShowSecondaryOwners parameter name needs to be renamed, it should be renamed something similar to showAdditionalInfo
export const WorkPackageOwnerList: FC<WorkPackageOwnerListProps> = ({
  primaryOwners,
  secondaryOwners,
  otherOwners,
  showSecondaryOwners,
  workPackage,
}) => {
  const [secondaryOwnersVisible, setSecondaryOwnersVisible] = useState(false);
  const { visible, open, close } = useSideSheetState(false);
  const { canEditSuperintendent, canEditGeneralForeman, canEditForeman, canEditFieldEngineer } =
    useWorkPackagePermissions([...primaryOwners, ...secondaryOwners, ...otherOwners]);
  const canEditOwners = canEditSuperintendent || canEditGeneralForeman || canEditForeman || canEditFieldEngineer;
  return (
    <OwnerListContainerStyled>
      <OwnerListOuterStyled>
        <OwnerList owners={secondaryOwners} visible={secondaryOwnersVisible && isAnyOwnerAssigned(secondaryOwners)} />
        <OwnerList owners={otherOwners} visible={secondaryOwnersVisible && isAnyOwnerAssigned(otherOwners)} />
      </OwnerListOuterStyled>

      {showSecondaryOwners && (
        <>
          <Box justifyContent={{ sm: 'flex-end' }}>
            {canEditOwners && (
              <Box mr={{ sm: '400' }} data-testid="edit-ownership">
                <OwnerActionButtonStyled onClick={open}>
                  <ActionButtonContent icon="user-cog" buttonText={t`Edit Ownership`}></ActionButtonContent>
                </OwnerActionButtonStyled>
              </Box>
            )}
            {isAnyOwnerAssigned([...secondaryOwners, ...otherOwners]) && (
              <Box>
                <OwnerActionButtonStyled
                  onClick={() => {
                    setSecondaryOwnersVisible(!secondaryOwnersVisible);
                  }}
                  secondaryOwnersVisible={secondaryOwnersVisible}
                  style={{ zIndex: 1 }} // To overlay on owners panel
                >
                  <ActionButtonContent icon="users" buttonText={t`Secondary Owners`}></ActionButtonContent>
                  <Box ml={{ sm: '400' }}>
                    <Icon icon={secondaryOwnersVisible ? 'chevron-up' : 'chevron-down'} size={IconSize.MEDIUM} />
                  </Box>
                </OwnerActionButtonStyled>
              </Box>
            )}
          </Box>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            {visible && (
              <AssignWorkOwner
                onClose={close}
                assignedCrewMembers={primaryOwners}
                secondaryOwners={secondaryOwners}
                otherOwners={otherOwners}
                workPackages={[workPackage]}
              />
            )}
          </Suspense>
        </>
      )}
    </OwnerListContainerStyled>
  );
};
export const WorkPackagePrimaryOwnerList: FC<WorkPackagePrimaryOwnerListProps> = ({
  primaryOwners,
  isPlanningModule = false,
}) => {
  const { breakpoints } = useContext<Theme>(ThemeContext);
  const isDesktop = useMediaQuery(belowBreakpoint(breakpoints.desktop));
  return (
    <Box mr={{ sm: isPlanningModule && isDesktop ? '200' : '600' }}>
      <OwnerList owners={primaryOwners} visible />
    </Box>
  );
};
